import React, { useState, useEffect } from 'react';
import { Search, SortAsc, SortDesc, Edit3, Save, X, Check, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { getUserInfo } from './apiUtils';
import Dialog from './ManageUsersComponents/Dialog';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '0px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    fontSize: '0.875rem',
  },
  th: {
    backgroundColor: '#E5E4E2',
    color: '#0064e5',
    padding: '6px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '0.875rem',
    lineHeight: '1',
    height: '32px',
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
  },
  checkbox: {
    marginRight: '10px',
  },
  button: {
    padding: '6px 12px',
    margin: '0 5px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: {
    color: 'red',
    padding: '10px',
    marginBottom: '10px',
  },
};

const ManageAccessLevels = () => {
  const [categoryHeaders, setCategoryHeaders] = useState([]);
  const [accessLevelDefinitions, setAccessLevelDefinitions] = useState([]);
  const [accessLevelRoles, setAccessLevelRoles] = useState([]);
  const [selectedCategoryHeader, setSelectedCategoryHeader] = useState(null);
  const [selectedDefinitions, setSelectedDefinitions] = useState(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canViewAccessLevelDefinitions: false,
  });

  // Edit mode states for definitions
  const [editingRows, setEditingRows] = useState(new Set());
  const [rowEditData, setRowEditData] = useState({});
  const [savingRows, setSavingRows] = useState(new Set());

  // State for success/error messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const login_token = localStorage.getItem('authToken');
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');
  const [redirectPath, setRedirectPath] = useState('');

  // Helper function to format boolean values
  const formatBoolean = (value) => {
    return value ? 'Yes' : 'No';
  };

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };

  // Function to update access level definition via API
  const updateAccessLevelDefinition = async (categorySubHeader, accessLevels) => {
    try {
      const requestBody = {
        corporate_account: corporateAccount,
        category_header: selectedCategoryHeader.toUpperCase(),
        category_sub_header: categorySubHeader,
        ...accessLevels
      };

      console.log('Updating access level definition:', requestBody);

      const response = await fetch(appConfig.getUrl('/update_admin_access_level_definition'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        console.log('Access level definition updated successfully:', data.status_description);
        return { success: true, message: data.status_description };
      } else {
        console.error('Failed to update access level definition:', data.status_description);
        return { success: false, message: data.status_description || 'Update failed' };
      }
    } catch (error) {
      console.error('Error updating access level definition:', error);
      return { success: false, message: 'Network error occurred while updating' };
    }
  };

  // Function to handle inline cell editing
  const handleInlineCellEdit = async (categorySubHeader, levelName, newValue) => {
    if (!permissions.canUpdate) {
      setDialogMessage('You do not have permission to edit access level definitions');
      setShowDialog(true);
      return;
    }

    // Extract level number from level_name (e.g., "Level 1" -> "1", "Level 2" -> "2")
    const levelMatch = levelName.match(/\d+/);
    const levelNumber = levelMatch ? levelMatch[0] : null;

    if (!levelNumber) {
      console.error(`Could not extract level number from: ${levelName}`);
      setDialogMessage(`Invalid level name format: ${levelName}`);
      setShowDialog(true);
      return;
    }

    const levelKey = `level_${levelNumber}`;
    const accessLevels = {};

    // Set only the specific level being updated
    accessLevels[levelKey] = newValue;

    console.log(`Inline edit: ${levelName} -> ${levelKey} = ${newValue}`);
    console.log('AccessLevels object:', accessLevels);

    const result = await updateAccessLevelDefinition(categorySubHeader, accessLevels);

    if (result.success) {
      // Update the local state
      setAccessLevelDefinitions(prev =>
        prev.map(def =>
          def.category_sub_header === categorySubHeader
            ? { ...def, [levelName.toLowerCase()]: newValue }
            : def
        )
      );

      // Show success message
      setSuccessMessage(`Successfully updated ${categorySubHeader} - ${levelName}: ${newValue ? 'Yes' : 'No'}`);
      setErrorMessage('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setErrorMessage(`Failed to update access level: ${result.message}`);
      setSuccessMessage('');
    }
  };

  // Function to start row editing
  const startRowEdit = (categorySubHeader) => {
    if (!permissions.canUpdate) {
      setDialogMessage('You do not have permission to edit access level definitions');
      setShowDialog(true);
      return;
    }

    const definition = accessLevelDefinitions.find(def => def.category_sub_header === categorySubHeader);
    if (!definition) return;

    const editData = {};
    accessLevelRoles.forEach(role => {
      const levelKey = role.level_name.toLowerCase();
      editData[levelKey] = definition[levelKey] || false;
    });

    setEditingRows(prev => new Set([...prev, categorySubHeader]));
    setRowEditData(prev => ({
      ...prev,
      [categorySubHeader]: editData
    }));
  };

  // Function to cancel row editing
  const cancelRowEdit = (categorySubHeader) => {
    setEditingRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(categorySubHeader);
      return newSet;
    });

    setRowEditData(prev => {
      const newData = { ...prev };
      delete newData[categorySubHeader];
      return newData;
    });
  };

  // Function to save row edits
  const saveRowEdit = async (categorySubHeader) => {
    setSavingRows(prev => new Set([...prev, categorySubHeader]));

    const editData = rowEditData[categorySubHeader];
    if (!editData) {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(categorySubHeader);
        return newSet;
      });
      return;
    }

    // Convert to API format (level_1, level_2, etc.)
    const accessLevels = {};
    accessLevelRoles.forEach(role => {
      // Extract level number from level_name (e.g., "Level 1" -> "1", "Level 2" -> "2")
      const levelMatch = role.level_name.match(/\d+/);
      const levelNumber = levelMatch ? levelMatch[0] : null;

      if (levelNumber) {
        const levelKey = `level_${levelNumber}`;
        const editKey = role.level_name.toLowerCase();
        accessLevels[levelKey] = editData[editKey] || false;
        console.log(`Mapping ${role.level_name} (${editKey}) -> ${levelKey} = ${editData[editKey] || false}`);
      } else {
        console.warn(`Could not extract level number from: ${role.level_name}`);
      }
    });

    console.log('Final accessLevels object:', accessLevels);
    const result = await updateAccessLevelDefinition(categorySubHeader, accessLevels);

    setSavingRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(categorySubHeader);
      return newSet;
    });

    if (result.success) {
      // Update the local state
      setAccessLevelDefinitions(prev =>
        prev.map(def =>
          def.category_sub_header === categorySubHeader
            ? { ...def, ...editData }
            : def
        )
      );

      // Exit edit mode
      cancelRowEdit(categorySubHeader);

      // Show success message
      setSuccessMessage(`Successfully updated all access levels for: ${categorySubHeader}`);
      setErrorMessage('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } else {
      setErrorMessage(`Failed to update access levels: ${result.message}`);
      setSuccessMessage('');
    }
  };

  // Function to update row edit data
  const updateRowEditData = (categorySubHeader, levelKey, value) => {
    setRowEditData(prev => ({
      ...prev,
      [categorySubHeader]: {
        ...prev[categorySubHeader],
        [levelKey]: value
      }
    }));
  };

  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    console.log(`Checking permissions for ${categoryHeader} with sub-headers:`, categorySubHeaders);

    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: ""
        }),
      });
      const data = await response.json();
      console.log(`API response for ${categoryHeader}:`, data);

      const newPermissions = {};

      data.user_actions_list.forEach(item => {
        console.log(`Processing user action: ${item.user_action}, has_access: ${item.has_access}`);

        switch(item.user_action) {
          case 'Edit Access Level Definitions':
            newPermissions.canUpdate = item.has_access;
            break;
          case 'View Access Level Definitions':
            newPermissions.canViewAccessLevelDefinitions = item.has_access;
            console.log(`User has permission to view access level definitions: ${item.has_access}`);
            break;
        }
      });

      setPermissions(prevPermissions => ({
        ...prevPermissions,
        ...newPermissions
      }));

      // Return the new permissions so they can be used immediately
      return {
        success: data.access_status && data.status === 'Success',
        permissions: newPermissions
      };
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return {
        success: false,
        permissions: {}
      };
    }
  };

  const fetchCategoryHeaders = async () => {
    try {
      const response = await fetch(appConfig.getUrl('/get_access_level_category_headers'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setCategoryHeaders(data.category_header_list);
        console.log('Category headers fetched successfully:', data.category_header_list.length, 'headers');
      } else {
        console.error('Failed to fetch category headers:', data.status_description);
        setCategoryHeaders([]);
      }
    } catch (error) {
      console.error('Error fetching category headers:', error);
      setCategoryHeaders([]);
    }
  };

  const fetchAccessLevelRoles = async () => {
    try {
      const response = await fetch(appConfig.getUrl('/get_access_level_roles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAccessLevelRoles(data.access_level_roles);
        console.log('Access level roles fetched successfully:', data.access_level_roles.length, 'roles');
      } else {
        console.error('Failed to fetch access level roles:', data.status_description);
        setAccessLevelRoles([]);
      }
    } catch (error) {
      console.error('Error fetching access level roles:', error);
      setAccessLevelRoles([]);
    }
  };

  const fetchAccessLevelDefinitions = async (categoryHeader) => {
    setAccessLevelDefinitions([]);

    console.log('Fetching access level definitions for category:', categoryHeader);

    if (!permissions.canViewAccessLevelDefinitions) {
      console.log('User does not have permission to view access level definitions');
      return;
    }

    if (!categoryHeader) {
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_admin_access_level_definitions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          category_header: categoryHeader.toUpperCase()
        })
      });

      const data = await response.json();

      console.log('API response for access level definitions:', data);
      
      if (data.status === 'Success') {
        let filteredDefinitions = data.category_sub_header_list;

        if (searchFilter) {
          filteredDefinitions = filteredDefinitions.filter(definition =>
            definition.category_sub_header.toLowerCase().includes(searchFilter.toLowerCase())
          );
        }

        if (sortConfig.key) {
          filteredDefinitions.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }

        setAccessLevelDefinitions(filteredDefinitions);
      } else {
        console.error('Failed to fetch access level definitions:', data.status_description);
        setAccessLevelDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching access level definitions:', error);
      setAccessLevelDefinitions([]);
    }
  };

  const handleCategoryHeaderSelect = (categoryHeader) => {
    // Clear any ongoing edits when switching categories
    setEditingRows(new Set());
    setRowEditData({});
    setSavingRows(new Set());

    setSelectedCategoryHeader(categoryHeader);
    setSelectedDefinitions(new Set());
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = () => {
    setSearchFilter(searchInput);
  };

  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    setSearchInput(newValue);

    if (newValue === '') {
      setSearchFilter('');
    }
  };

  const getSelectedCategoryHeaderDisplay = () => {
    return selectedCategoryHeader || '';
  };

  const refreshDefinitions = () => {
    if (permissions.canViewAccessLevelDefinitions && selectedCategoryHeader) {
      fetchAccessLevelDefinitions(selectedCategoryHeader);
    }
    setSelectedDefinitions(new Set());

    setEditingRows(new Set());
    setRowEditData({});
    setSavingRows(new Set());

    // Also refresh roles
    if (corporateAccount) {
      fetchAccessLevelRoles();
    }
  };

  // Get role name by level
  const getRoleNameByLevel = (levelName) => {
    const role = accessLevelRoles.find(role => role.level_name === levelName);
    return role ? role.role_name : levelName;
  };

  // Initial load useEffect - Get user info
  useEffect(() => {
    const setInitialLoad = async () => {
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }

      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setCorporateAccount(result.userInfo.corporate_account);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(result.userInfo.default_project || 'default');
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  // Main initialization useEffect - Check permissions and fetch data
  useEffect(() => {
    const initializeApp = async () => {
      if (!userId || !corporateAccount) return;

      try {
        console.log('Starting app initialization...');

        // Clear data first to prevent race conditions
        setCategoryHeaders([]);
        setAccessLevelRoles([]);

        // Step 1: Check admin permissions
        console.log('Checking admin permissions...');
        const adminResult = await checkPermission('Admin Functions', ['Edit Access Level Definitions', 'View Access Level Definitions']);

        // Step 2: Fetch category headers and roles
        console.log('Fetching category headers and roles...');
        await fetchCategoryHeaders();
        await fetchAccessLevelRoles();

        console.log('App initialization complete');

      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };

    initializeApp();
  }, [userId, corporateAccount]);

  // Definitions fetching useEffect
  useEffect(() => {
    console.log('useEffect for selectedCategoryHeader, searchFilter, sortConfig, permissions.canViewAccessLevelDefinitions triggered --', selectedCategoryHeader);
    console.log('can View Access Level Definitions Permission:', permissions.canViewAccessLevelDefinitions);

    if (selectedCategoryHeader && permissions.canViewAccessLevelDefinitions) {
      console.log('Fetching access level definitions for selected category:', selectedCategoryHeader);
      fetchAccessLevelDefinitions(selectedCategoryHeader);
    } else if (selectedCategoryHeader && !permissions.canViewAccessLevelDefinitions) {
      setAccessLevelDefinitions([]);
    }
  }, [selectedCategoryHeader, searchFilter, sortConfig, permissions.canViewAccessLevelDefinitions]);

  // Auto-select category if only one exists
  useEffect(() => {
    if (categoryHeaders.length === 1 && !selectedCategoryHeader) {
      const singleCategory = categoryHeaders[0];
      console.log('Auto-selecting single category:', singleCategory.category_header);
      setSelectedCategoryHeader(singleCategory.category_header);
      setSelectedDefinitions(new Set());
      setCurrentPage(1);
    }
  }, [categoryHeaders, selectedCategoryHeader]);

  // Pagination useEffects
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, sortConfig]);

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(accessLevelDefinitions.length / itemsPerPage);
    if (currentPage > Math.max(1, calculatedTotalPages)) {
      setCurrentPage(Math.max(1, calculatedTotalPages));
    }
  }, [accessLevelDefinitions, currentPage, itemsPerPage]);

  // Add pagination calculation logic
  const totalPages = Math.ceil(accessLevelDefinitions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDefinitions = accessLevelDefinitions.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div style={{ marginTop: '80px' }}>
        {/* Header section */}
        <div className="pt-2 pb-2">
          <div className="flex items-center px-6 mb-0 justify-between">
            <div className="w-80">
              <div className="flex items-center relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search access level definitions..."
                  className="p-2 pr-4 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
                  aria-label="Search"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            <h1 className="text-xl font-bold text-center flex-1">Manage Access Levels</h1>

            <div className="w-80 flex justify-end">
              <Link
                to="/UserRolesDefinition"
                className="flex items-center px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 mr-2 no-underline"
                title="Manage Role Definitions"
              >
                <Settings size={16} className="mr-1" />
                <span>Manage Roles</span>
              </Link>
              <button
                onClick={refreshDefinitions}
                className="flex items-center px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 mr-2"
              >
                <span>Refresh</span>
              </button>
            </div>
          </div>

          <div className="flex items-center px-6 mb-0 bg-blue-100 py-0 rounded-lg">
            {/* Selected Category Path - Left aligned */}
            <div className="flex-1 text-left">
              <span className="text-base font-bold text-blue-700">Selected Category: </span>
              <span className="text-sm">
                {selectedCategoryHeader ? (
                  <>
                    <span className="text-green-600">
                      {getSelectedCategoryHeaderDisplay()}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedCategoryHeader(null);
                        setAccessLevelDefinitions([]);
                        // Clear edit states
                        setEditingRows(new Set());
                        setRowEditData({});
                        setSavingRows(new Set());
                      }}
                      className="px-3 py-1 text-xs bg-red-300 text-white rounded hover:bg-red-500 items-center ml-4">
                      X Clear Selection
                    </button>
                  </>
                ) : (
                  <span style={{ color: 'red' }}>None Selected</span>
                )}
              </span>
            </div>

            {/* Current User Info - Center aligned */}
            <div className="flex-1 text-center">
              <h2 className="text-base font-bold text-blue-700"> These Access Level Definitions Applies To All Projects </h2>
            </div>

            {/* Edit mode info - Right aligned */}
            <div className="flex-1 flex justify-end items-center">
              {editingRows.size > 0 && (
                <div className="bg-yellow-100 border border-yellow-300 px-3 py-1 rounded text-sm text-yellow-800">
                  Editing {editingRows.size} row{editingRows.size > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Category Headers */}
        <div className="mt-0 w-64 text-white bg-blue-800 border-r overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-center items-center mb-4 bg-white p-2 rounded-lg shadow">
              <h2 className="font-semibold text-sm text-blue-600">Select Category</h2>
            </div>

            {categoryHeaders.map(category => (
              <div
                key={category.category_header}
                className={`flex items-center py-2 cursor-pointer ${
                  selectedCategoryHeader === category.category_header ? 'bg-blue-100' : ''
                }`}
                style={{
                  backgroundColor: selectedCategoryHeader === category.category_header ? '#DBEAFE' : 'transparent',
                  color: selectedCategoryHeader === category.category_header ? '#1E40AF' : 'white',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategoryHeader !== category.category_header) {
                    e.currentTarget.style.backgroundColor = '#fcfafc';
                    e.currentTarget.style.color = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategoryHeader !== category.category_header) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onClick={() => handleCategoryHeaderSelect(category.category_header)}
              >
                <span className="ml-2 text-sm">
                  {category.category_header}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-0 relative">
          {!permissions.canViewAccessLevelDefinitions ? (
            <div className="flex items-center justify-center h-full">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-yellow-800 mb-3 text-center">Access Restricted</h3>
                <p className="text-yellow-700 text-center">
                  You do not have permission to view access level definitions. Please contact your administrator to request access.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1">
              <div className="bg-white rounded-lg shadow">
                <table style={styles.table}>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th
                        style={styles.th}
                        onClick={() => handleSort('category_sub_header')}
                        className="select-none hover:bg-gray-200 transition-colors"
                      >
                        <div className="flex items-center">
                          <span style={{ textDecoration: 'underline' }}>Function</span>
                          <span className="ml-1">
                            {sortConfig.key === 'category_sub_header' ? (
                              sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                            ) : null}
                          </span>
                        </div>
                      </th>
                      {accessLevelRoles.map(role => (
                        <th
                          key={role.level_name}
                          style={{...styles.th, height: 'auto', padding: '4px 8px'}}
                          onClick={() => handleSort(role.level_name.toLowerCase())}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex flex-col items-center">
                            <div className="flex items-center">
                              <span style={{ textDecoration: 'underline', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                {role.level_name}
                              </span>
                              <span className="ml-1">
                                {sortConfig.key === role.level_name.toLowerCase() ? (
                                  sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                                ) : null}
                              </span>
                            </div>
                            <div className="text-xs mt-1" style={{ fontSize: '0.7rem', lineHeight: '1' }}>
                              {role.role_name}
                            </div>
                          </div>
                        </th>
                      ))}
                      {/* Actions column header */}
                      {permissions.canUpdate && (
                        <th style={{...styles.th, textAlign: 'center', width: '100px'}}>
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDefinitions.map(definition => {
                      const isEditing = editingRows.has(definition.category_sub_header);
                      const isSaving = savingRows.has(definition.category_sub_header);
                      const editData = rowEditData[definition.category_sub_header] || {};

                      return (
                        <tr
                          key={definition.category_sub_header}
                          className={`border-t ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'} ${isSaving ? 'opacity-50' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <span className={`font-medium ${isEditing ? 'text-blue-800' : 'text-blue-600'}`}>
                              {definition.category_sub_header}
                              {isEditing && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  Editing
                                </span>
                              )}
                              {isSaving && (
                                <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                  Saving...
                                </span>
                              )}
                            </span>
                          </td>
                          {accessLevelRoles.map(role => {
                            const levelKey = role.level_name.toLowerCase();
                            const hasAccess = isEditing ? editData[levelKey] : definition[levelKey];

                            return (
                              <td key={role.level_name} className="px-4 py-3 text-center">
                                {isEditing ? (
                                  // Row edit mode - show toggle buttons
                                  <button
                                    onClick={() => updateRowEditData(definition.category_sub_header, levelKey, !hasAccess)}
                                    disabled={isSaving}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                      hasAccess
                                        ? 'bg-green-200 text-green-800 hover:bg-green-300'
                                        : 'bg-red-200 text-red-800 hover:bg-red-300'
                                    } ${isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                  >
                                    {formatBoolean(hasAccess)}
                                  </button>
                                ) : permissions.canUpdate ? (
                                  // Inline edit mode - clickable cells
                                  <button
                                    onClick={() => handleInlineCellEdit(definition.category_sub_header, role.level_name, !hasAccess)}
                                    disabled={isSaving || editingRows.size > 0}
                                    className={`px-2 py-1 rounded-full text-xs transition-colors ${
                                      hasAccess
                                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                                    } ${(isSaving || editingRows.size > 0) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'}`}
                                    title={`Click to toggle ${role.level_name} access`}
                                  >
                                    {formatBoolean(hasAccess)}
                                  </button>
                                ) : (
                                  // Read-only mode
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    hasAccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {formatBoolean(hasAccess)}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          {/* Actions column */}
                          {permissions.canUpdate && (
                            <td className="px-4 py-3 text-center">
                              {isEditing ? (
                                <div className="flex justify-center gap-1">
                                  <button
                                    onClick={() => saveRowEdit(definition.category_sub_header)}
                                    disabled={isSaving}
                                    className={`p-1 rounded text-white transition-colors ${
                                      isSaving
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-green-500 hover:bg-green-600'
                                    }`}
                                    title="Save changes"
                                  >
                                    {isSaving ? (
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                      <Check size={16} />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => cancelRowEdit(definition.category_sub_header)}
                                    disabled={isSaving}
                                    className={`p-1 rounded text-white transition-colors ${
                                      isSaving
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-red-500 hover:bg-red-600'
                                    }`}
                                    title="Cancel editing"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startRowEdit(definition.category_sub_header)}
                                  disabled={isSaving || editingRows.size > 0}
                                  className={`p-1 rounded text-white transition-colors ${
                                    (isSaving || editingRows.size > 0)
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : 'bg-blue-500 hover:bg-blue-600'
                                  }`}
                                  title="Edit entire row"
                                >
                                  <Edit3 size={16} />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {accessLevelDefinitions.length === 0 && (
                      <tr>
                        <td colSpan={accessLevelRoles.length + (permissions.canUpdate ? 2 : 1)} className="px-4 py-8 text-center text-gray-500">
                          No access level definitions found. {selectedCategoryHeader ? 'Adjust your search or select a different category.' : 'Select a category to view access level definitions.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Success/Error Messages */}
              {(successMessage || errorMessage) && (
                <div className={`mt-4 p-3 rounded-lg ${
                  successMessage
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {successMessage || errorMessage}
                </div>
              )}

              {/* Edit Instructions */}
              {permissions.canUpdate && accessLevelDefinitions.length > 0 && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-800 mb-2">Edit Instructions:</h3>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div><strong>Access Level Editing:</strong> Click any access level cell (Yes/No) to toggle it immediately</div>
                    <div><strong>Row Editing:</strong> Click the <Edit3 size={14} className="inline" /> button to edit all access levels for a function at once</div>
                    <div><strong>Role Management:</strong> Click the "Manage Roles" button to edit role names and definitions</div>
                    <div><strong>Note:</strong> Changes are saved automatically. You cannot edit multiple rows simultaneously.</div>
                  </div>
                </div>
              )}

              {/* Pagination and definition count information */}
              {accessLevelDefinitions.length > 0 && (
                <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Total Definitions: {accessLevelDefinitions.length}</span>
                    {editingRows.size > 0 && (
                      <span>| Editing: {editingRows.size}</span>
                    )}
                    <span>| Showing {accessLevelDefinitions.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, accessLevelDefinitions.length)} of {accessLevelDefinitions.length}</span>
                  </div>

                  {/* Pagination controls */}
                  <div className="flex items-center gap-2">
                    <button
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                    <button
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      aria-label="Next page"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Items per page selector */}
                  <div className="flex items-center">
                    <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border rounded p-1 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialog components */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={() => {
            setShowDialog(false);
          }}
          onCancel={() => setShowDialog(false)}
        />
      )}
    </div>
  );
};

export default ManageAccessLevels;